import { InjectionToken, Signal, signal, WritableSignal } from '@angular/core';
import { BadgeEmphasis } from '@pure/hive';
import Highcharts from 'highcharts/highstock';
import moment from 'moment';
import { Subject } from 'rxjs';
import { WorkflowRecipe } from './incident';

// TODO: Union other recommendation types as they are supported
export type RecommendationRenderInfo =
    | ContractRenewalRenderInfo
    | EoneTierUpgradeRenderInfo
    | EoneCapacityExpansionRenderInfo
    | NvmeNowRenderInfo
    | WorkflowRecipeRenderInfo
    | EvercycleBuybackRenderInfo
    | FbNorwayEolRenderInfo
    | EoscFbRenderInfo;
export type RecommendationHeaderEmphasis = 'warning' | 'danger' | 'success';

export type ContractRenewalEmphasis = 'warning' | 'danger';

/**
 * Enum for all recommendation action events across all recommendation types.
 *
 * Note: Not all events are applicable to all recommendation types. Each recommendation type
 * uses only the subset of actions that are relevant to its functionality.
 *
 * When to modify this enum:
 * - Add new actions when a recommendation type requires new user interactions
 * - Adding a new recommendation type does NOT require changes here unless it introduces new actions
 */
export enum RecommendationActionEvent {
    CANCEL = 'cancel',
    CARD_CLICKED = 'card-clicked',
    CONTACT_ACCOUNT_TEAM = 'contact-account-team',
    DISMISS = 'dismiss',
    /** A recipe was imported from the workflow-recipe dialog. */
    RECIPE_IMPORTED = 'recipe-imported',
    DOWNLOAD_QUOTE = 'download-quote',
    /**
     * Request the container to reload the current recommendation from the feed
     * and re-bind the dialog to the refreshed `renderInfo`. First consumer is
     * the EverCycle accept flow on `OFFER_MISMATCH` so the next Accept posts
     * against the fresh `offer_id` instead of looping on the stale one.
     */
    REFRESH = 'refresh',
    RENEW_CONTRACT = 'renew-contract',
    REQUEST_QUOTE = 'request-quote',
    REQUEST_QUOTE_BY_EMAIL = 'request-quote-by-email',
    UPLOAD_PO = 'upload-po',
}

/**
 * Injection token for recommendation action events across all recommendation types.
 *
 * This token provides a communication channel between dialog plugins and their container.
 * Dialog plugins inject this token to emit action events (from RecommendationActionEvent enum).
 * The container provides this token via a custom injector and subscribes to handle the events.
 *
 * When to use:
 * - Use this token in dialog plugins when you need to emit action events to the container
 * - Dialog plugins are dynamically created components that need to communicate user actions back to the container
 *
 * Note: Each recommendation type uses only the subset of actions that are relevant to its functionality.
 * Adding a new recommendation type does NOT require changes to this token - it remains the same
 * communication mechanism regardless of which specific actions are being emitted.
 *
 * @example
 * // In dialog plugin:
 * private readonly actionEvent$ = inject<Subject<RecommendationActionEvent>>(RECOMMENDATION_ACTION_EVENT_TOKEN, { optional: true });
 * this.actionEvent$?.next(RecommendationActionEvent.REQUEST_QUOTE_BY_EMAIL);
 *
 * // In container:
 * providers: [{ provide: RECOMMENDATION_ACTION_EVENT_TOKEN, useValue: this.recommendationActionEvent$ }]
 */
export const RECOMMENDATION_ACTION_EVENT_TOKEN = new InjectionToken<Subject<RecommendationActionEvent>>(
    'RecommendationActionEvent',
);

export interface ContractAddOn {
    header: string;
    subheader: string;
    iconUrl: string;
    priceTagPerYear: string;
}

export interface ContractRenewalTableRow {
    applianceName: string;
    model: string;
    subscriptionType: string;
    expiryRemainingDays: string;
    expirationDateUtc: moment.Moment;
}

/**
 * This object contains data for visual representation of a recommendation.
 * Successful Recommendation object -> RenderInfo conversion should mean we can render it on the page.
 * Specific recommendation types extend this class to add their specific info
 */
abstract class BaseRecommendationRenderInfo {
    readonly cardPluginComponentName: string;
    readonly dialogPluginComponentName: string;
    readonly id: string;
    readonly targetName: string;
    readonly recommendationHeader: {
        label: string;
        emphasis: RecommendationHeaderEmphasis;
    };
    readonly subHeaderLabel?: string;
    readonly allowQuoteDownload: WritableSignal<boolean>;
    readonly allowDismiss: WritableSignal<boolean>;
    readonly allowEmailQuote: WritableSignal<boolean>;
    readonly allowPurchaseOrderUpload: WritableSignal<boolean>;
    readonly allowRequestQuote: WritableSignal<boolean>;
    readonly allowContactAccountTeam: WritableSignal<boolean>;
    /**
     * When true, the card-template shell hides the entire action-button row
     * (including the always-on "Details" button). Use for recommendations whose
     * primary action lives only inside the details dialog — e.g. NVMe Now, where
     * "Contact Account Team" is a dialog-only action.
     */
    readonly hideActionButtons?: boolean;
    /**
     * Renders the shell's "Details" button, which opens the dialog. Opt-in: most cards
     * rely on the whole card being clickable and show quote actions in the footer
     * instead.
     */
    readonly showDetailsButton?: boolean;
    /**
     * Opt-in shell layout: the emphasis line moves out of the nested panel into the
     * header, rendered as a hive-label so its emphasis colour applies, and the nested
     * panel's content sits at the top instead of being centred. Off by default so the
     * existing cards keep their original layout.
     */
    readonly emphasisInHeader?: boolean;
    /**
     * When true, the card-template shell hides the recommendation-header pill
     * (rendered below the target name). Use when the plugin body already
     * conveys the recommendation type visually and the pill would be redundant.
     */
    readonly hideRecommendationHeader?: boolean;
    /**
     * Optional icon rendered by the shell above the target name (mirrors the
     * legacy UpgradeToForever/ForeverNow card layout). Value is a filename
     * resolved by hive's SvgDirective against the assets/icons/ tree —
     * e.g. `'everpure/evergreen.svg'`.
     */
    readonly topIconSvg?: string;
    /** Set to true while an async action (e.g. create quote) is in flight — disables all action buttons. */
    readonly actionInProgress: WritableSignal<boolean> = signal(false);
    readonly audienceWorkFlowStatus?: {
        label: string;
        emphasis: BadgeEmphasis;
    };
    readonly partnerName?: string;
    readonly priceTag?: string;
    readonly reason?: string;

    protected constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        subHeaderLabel?: string;
        allowQuoteDownload?: WritableSignal<boolean>;
        allowDismiss?: WritableSignal<boolean>;
        allowEmailQuote?: WritableSignal<boolean>;
        allowPurchaseOrderUpload?: WritableSignal<boolean>;
        allowRequestQuote?: WritableSignal<boolean>;
        allowContactAccountTeam?: WritableSignal<boolean>;
        hideActionButtons?: boolean;
        showDetailsButton?: boolean;
        emphasisInHeader?: boolean;
        hideRecommendationHeader?: boolean;
        topIconSvg?: string;
        audienceWorkFlowStatus?: {
            label: string;
            emphasis: BadgeEmphasis;
        };
        partnerName?: string;
        priceTag?: string;
        reason?: string;
    }) {
        if (!params.cardPluginComponentName) {
            throw new Error('BaseRenderInfo: cardPluginComponentName is required');
        }
        if (!params.dialogPluginComponentName) {
            throw new Error('BaseRenderInfo: dialogPluginComponentName is required');
        }
        if (!params.id) {
            throw new Error('BaseRenderInfo: id is required');
        }
        if (!params.targetName) {
            throw new Error('BaseRenderInfo: targetName is required');
        }
        if (!params.recommendationHeader) {
            throw new Error('BaseRenderInfo: recommendationHeader is required');
        }
        if (!params.recommendationHeader.label) {
            throw new Error('BaseRenderInfo: recommendationHeader.label is required');
        }
        if (!params.recommendationHeader.emphasis) {
            throw new Error('BaseRenderInfo: recommendationHeader.emphasis is required');
        }
        if (params.audienceWorkFlowStatus) {
            if (!params.audienceWorkFlowStatus.label) {
                throw new Error(
                    'BaseRenderInfo: audienceWorkFlowStatus.label is required when audienceWorkFlowStatus is provided',
                );
            }
            if (!params.audienceWorkFlowStatus.emphasis) {
                throw new Error(
                    'BaseRenderInfo: audienceWorkFlowStatus.emphasis is required when audienceWorkFlowStatus is provided',
                );
            }
        }
        // Validate optional fields: if provided, they should not be empty
        if (params.partnerName?.trim() === '') {
            throw new Error('BaseRenderInfo: partnerName must be a non-empty string when provided');
        }
        if (params.priceTag?.trim() === '') {
            throw new Error('BaseRenderInfo: priceTag must be a non-empty string when provided');
        }
        if (params.subHeaderLabel?.trim() === '') {
            throw new Error('BaseRenderInfo: subHeaderLabel must be a non-empty string when provided');
        }
        if (params.reason?.trim() === '') {
            throw new Error('BaseRenderInfo: reason must be a non-empty string when provided');
        }

        this.cardPluginComponentName = params.cardPluginComponentName;
        this.dialogPluginComponentName = params.dialogPluginComponentName;
        this.id = params.id;
        this.targetName = params.targetName;
        this.recommendationHeader = params.recommendationHeader;
        this.subHeaderLabel = params.subHeaderLabel;
        this.allowQuoteDownload = params.allowQuoteDownload;
        this.allowDismiss = params.allowDismiss;
        this.allowEmailQuote = params.allowEmailQuote;
        this.allowPurchaseOrderUpload = params.allowPurchaseOrderUpload;
        this.allowRequestQuote = params.allowRequestQuote;
        this.allowContactAccountTeam = params.allowContactAccountTeam;
        this.hideActionButtons = params.hideActionButtons;
        this.showDetailsButton = params.showDetailsButton;
        this.emphasisInHeader = params.emphasisInHeader;
        this.hideRecommendationHeader = params.hideRecommendationHeader;
        this.topIconSvg = params.topIconSvg;
        this.audienceWorkFlowStatus = params.audienceWorkFlowStatus;
        this.partnerName = params.partnerName;
        this.priceTag = params.priceTag;
        this.reason = params.reason;
    }

    // Computed getters for template binding
    get recommendationHeaderLabel(): string {
        return this.recommendationHeader.label;
    }

    get recommendationHeaderEmphasis(): RecommendationHeaderEmphasis {
        return this.recommendationHeader.emphasis;
    }

    get audienceWorkFlowStatusLabel(): string | undefined {
        return this.audienceWorkFlowStatus?.label;
    }

    get audienceWorkflowStatusEmphasis(): BadgeEmphasis | undefined {
        return this.audienceWorkFlowStatus?.emphasis;
    }

    get asPluginInput(): Record<string, unknown> {
        return { renderInfo: this };
    }
}

/**
 * ================================================
 * Additional render info for contract renewal recommendations
 * ================================================
 */

export class ContractRenewalRenderInfo extends BaseRecommendationRenderInfo {
    readonly headline: string;
    readonly detailedDescription: string;
    readonly daysRemaining: number;
    readonly emphasis: ContractRenewalEmphasis;
    readonly currentBenefitsList: [string, string][];
    readonly contractImgUrl: string;
    readonly contractTargets: ContractRenewalTableRow[];
    readonly addOns?: ContractAddOn[];
    readonly termDetails?: string;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        subHeaderLabel?: string;
        headline: string;
        detailedDescription: string;
        daysRemaining: number;
        emphasis: ContractRenewalEmphasis;
        currentBenefitsList: [string, string][];
        contractImgUrl: string;
        contractTargets: ContractRenewalTableRow[];
        addOns?: ContractAddOn[];
        allowQuoteDownload: WritableSignal<boolean>;
        allowPurchaseOrderUpload: WritableSignal<boolean>;
        allowDismiss: WritableSignal<boolean>;
        allowEmailQuote: WritableSignal<boolean>;
        audienceWorkFlowStatus?: {
            label: string;
            emphasis: BadgeEmphasis;
        };
        termDetails?: string;
        partnerName?: string;
        priceTag?: string;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            subHeaderLabel: params.subHeaderLabel,
            allowQuoteDownload: params.allowQuoteDownload,
            allowPurchaseOrderUpload: params.allowPurchaseOrderUpload,
            allowDismiss: params.allowDismiss,
            allowEmailQuote: params.allowEmailQuote,
            audienceWorkFlowStatus: params.audienceWorkFlowStatus,
            partnerName: params.partnerName,
            priceTag: params.priceTag,
        });

        // Validate required signal fields
        if (!params.allowQuoteDownload) {
            throw new Error('ContractRenewalRenderInfo: allowQuoteDownload is required');
        }
        if (!params.allowPurchaseOrderUpload) {
            throw new Error('ContractRenewalRenderInfo: allowPurchaseOrderUpload is required');
        }
        if (!params.allowDismiss) {
            throw new Error('ContractRenewalRenderInfo: allowDismiss is required');
        }
        if (!params.allowEmailQuote) {
            throw new Error('ContractRenewalRenderInfo: allowEmailQuote is required');
        }

        if (!params.headline) {
            throw new Error('ContractRenewalRenderInfo: headline is required');
        }
        if (!params.detailedDescription) {
            throw new Error('ContractRenewalRenderInfo: detailedDescription is required');
        }
        if (params.daysRemaining === undefined) {
            throw new Error('ContractRenewalRenderInfo: daysRemaining is required');
        }
        if (!params.emphasis) {
            throw new Error('ContractRenewalRenderInfo: emphasis is required');
        }
        if (!params.currentBenefitsList) {
            throw new Error('ContractRenewalRenderInfo: currentBenefitsList is required');
        }
        if (!params.contractImgUrl) {
            throw new Error('ContractRenewalRenderInfo: contractImgUrl is required');
        }
        if (!params.contractTargets) {
            throw new Error('ContractRenewalRenderInfo: contractTargets is required');
        }
        if (params.addOns) {
            params.addOns.forEach((addOn, index) => {
                if (!addOn.header) {
                    throw new Error(`ContractRenewalRenderInfo: addOns[${index}].header is required`);
                }
                if (!addOn.subheader) {
                    throw new Error(`ContractRenewalRenderInfo: addOns[${index}].subheader is required`);
                }
                if (!addOn.iconUrl) {
                    throw new Error(`ContractRenewalRenderInfo: addOns[${index}].iconUrl is required`);
                }
                if (!addOn.priceTagPerYear) {
                    throw new Error(`ContractRenewalRenderInfo: addOns[${index}].priceTagPerYear is required`);
                }
            });
        }
        // Validate optional fields: if provided, they should not be empty
        if (params.termDetails?.trim() === '') {
            throw new Error('ContractRenewalRenderInfo: termDetails must be a non-empty string when provided');
        }

        this.headline = params.headline;
        this.detailedDescription = params.detailedDescription;
        this.daysRemaining = params.daysRemaining;
        this.emphasis = params.emphasis;
        this.currentBenefitsList = params.currentBenefitsList;
        this.contractImgUrl = params.contractImgUrl;
        this.addOns = params.addOns;
        this.termDetails = params.termDetails;
        this.contractTargets = params.contractTargets;
    }
}

/**
 * ================================================
 * Additional render info for Evergreen//One tier upgrade recommendations
 * ================================================
 */

/** Represents a single value in the comparison table (e.g., "Bandwidth: 500 Gb/s") */
export interface ComparisonValue {
    /** The display text (e.g., "Bandwidth: 500 Gb/s") */
    readonly text: string;
    /** Whether this value should be styled as a warning (red) */
    readonly isWarning?: boolean;
    /**
     * Whether a recommended-column value is an improvement over the current
     * value. Default: `undefined` / `true` — render with the green up-arrow
     * upgrade styling. Set to `false` explicitly when the recommendation buys
     * no improvement for this row, so the dialog drops the upgrade styling.
     */
    readonly isImproved?: boolean;
    /**
     * Direction the trend arrow on a recommended-column value points. This is
     * the geometry of the arrow only — independent of colour, which conveys
     * sentiment. `'up'` (the default when omitted) suits metrics that rise with
     * the upgrade (bandwidth, IOPS); `'down'` suits a metric that falls and
     * where the fall is the win (projected utilization dropping toward its SLA),
     * so the arrow points down while staying green.
     */
    readonly arrowDirection?: 'up' | 'down';
}

/** Represents a row in the comparison table */
export interface ComparisonRow {
    /** Row label (e.g., "Max. Supported Load", "Projected utilization in 90 days") */
    readonly label: string;
    /** Current tier value — omitted when the row has sub-rows */
    readonly currentValue?: ComparisonValue;
    /** Recommended tier value — omitted when the row has sub-rows */
    readonly recommendedValue?: ComparisonValue;
    /** Optional sub-rows (e.g., Bandwidth and IOPS under "Max. Supported Load") */
    readonly subRows?: ComparisonRow[];
}

export class EoneTierUpgradeRenderInfo extends BaseRecommendationRenderInfo {
    /** Display name for the current license type (e.g., "UBF Capacity") */
    readonly currentLicenseType: string;
    /** Display name for the recommended license type (e.g., "UBF Performance") */
    readonly recommendedLicenseType: string;
    /**
     * Current license type for the "Upgrade from … to …" title only, with the
     * bandwidth SLA appended for Adaptive tiers (e.g. "UBF Adaptive 5 GB/s").
     * Defaults to {@link currentLicenseType} when not supplied. The plain
     * {@link currentLicenseType} is still used for the table columns and chart
     * legend, so the bandwidth appears in the title alone.
     */
    readonly currentLicenseTypeWithBandwidth: string;
    /** Recommended license type for the title only — see {@link currentLicenseTypeWithBandwidth}. */
    readonly recommendedLicenseTypeWithBandwidth: string;
    /** SVG file name for the recommended license type catalog offering icon */
    readonly recommendedLicenseSvg: string;
    /** Rows for the current vs recommended comparison table */
    readonly comparisonRows: ComparisonRow[];
    /**
     * Pre-built Highcharts options for the load chart. All data, styling, and
     * the asOf plot-line are baked in by the service — the dialog plugin just
     * mounts `new Chart(chartOptions)`.
     */
    readonly chartOptions: Highcharts.Options;
    /** Timestamp (epoch ms) — last point in historical load data */
    readonly asOfTime: number;
    /** Optional tooltip text shown when reserve commit changes */
    readonly recommendedTooltipText?: string;
    /**
     * True when the recommendation includes a reserve commit increase.
     * Gates the "(after reserve increase)" caption on the projected-load
     * legend row — without an increase, that line is just the current-tier
     * projection.
     */
    readonly hasReserveIncrease: boolean;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        currentLicenseType: string;
        recommendedLicenseType: string;
        currentLicenseTypeWithBandwidth?: string;
        recommendedLicenseTypeWithBandwidth?: string;
        recommendedLicenseSvg: string;
        reason: string;
        comparisonRows: ComparisonRow[];
        chartOptions?: Highcharts.Options;
        asOfTime?: number;
        recommendedTooltipText?: string;
        hasReserveIncrease?: boolean;
        allowDismiss?: WritableSignal<boolean>;
        allowRequestQuote?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            reason: params.reason,
            allowDismiss: params.allowDismiss ?? signal(false),
            allowRequestQuote: params.allowRequestQuote ?? signal(true),
        });

        if (!params.currentLicenseType?.trim()) {
            throw new Error('EoneTierUpgradeRenderInfo: currentLicenseType is required and must be non-empty');
        }
        if (!params.recommendedLicenseType?.trim()) {
            throw new Error('EoneTierUpgradeRenderInfo: recommendedLicenseType is required and must be non-empty');
        }
        if (!params.recommendedLicenseSvg?.trim()) {
            throw new Error('EoneTierUpgradeRenderInfo: recommendedLicenseSvg is required and must be non-empty');
        }
        if (!params.reason?.trim()) {
            throw new Error('EoneTierUpgradeRenderInfo: reason is required and must be non-empty');
        }

        this.currentLicenseType = params.currentLicenseType;
        this.recommendedLicenseType = params.recommendedLicenseType;
        this.currentLicenseTypeWithBandwidth = params.currentLicenseTypeWithBandwidth ?? params.currentLicenseType;
        this.recommendedLicenseTypeWithBandwidth =
            params.recommendedLicenseTypeWithBandwidth ?? params.recommendedLicenseType;
        this.recommendedLicenseSvg = params.recommendedLicenseSvg;
        this.comparisonRows = params.comparisonRows;
        this.chartOptions = params.chartOptions ?? {};
        this.asOfTime = params.asOfTime ?? 0;
        this.recommendedTooltipText = params.recommendedTooltipText;
        this.hasReserveIncrease = params.hasReserveIncrease ?? false;
    }
}

/**
 * ================================================
 * Additional render info for Evergreen//One capacity expansion recommendations
 * ================================================
 */

export class EoneCapacityExpansionRenderInfo extends BaseRecommendationRenderInfo {
    /** Display name for the service tier (e.g., "Unified Block and File - Capacity") */
    readonly serviceTier: string;
    /** Current reserved capacity in TiB — used in the headline */
    readonly currentReserveTib: number;
    /** Recommended reserved capacity in TiB — used in the headline */
    readonly recommendedReserveTib: number;
    /** Rows for the current vs recommended comparison table */
    readonly comparisonRows: ComparisonRow[];
    /** Projected percent of reserve at the threshold horizon (e.g., 157) */
    readonly projectedUsagePercent: number;
    /** Horizon in days that the threshold is evaluated against (e.g., 90) */
    readonly thresholdHorizonDays: number;
    /** Days until current reserve is exceeded; 0 means already exceeded */
    readonly daysToExceedCurrentReserve: number;
    /**
     * Pre-built Highcharts options for the capacity chart (dialog chart). All
     * data, styling, and the asOf plot-line are baked in by the service — the
     * dialog plugin just mounts `new Chart(chartOptions)`.
     */
    readonly chartOptions: Highcharts.Options;
    /**
     * Pre-built Highcharts options for the small card sparkline. All data,
     * styling, and the asOf plot-line are baked in by the service — the card
     * plugin just mounts `new Chart(sparklineOptions)`. Undefined when no
     * sparkline payload was sent.
     */
    readonly sparklineOptions?: Highcharts.Options;
    /** Timestamp (epoch ms) — last point in historical capacity data */
    readonly asOfTime: number;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        serviceTier: string;
        currentReserveTib: number;
        recommendedReserveTib: number;
        comparisonRows: ComparisonRow[];
        projectedUsagePercent: number;
        thresholdHorizonDays: number;
        daysToExceedCurrentReserve: number;
        chartOptions?: Highcharts.Options;
        sparklineOptions?: Highcharts.Options;
        asOfTime?: number;
        allowDismiss?: WritableSignal<boolean>;
        allowRequestQuote?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            allowDismiss: params.allowDismiss ?? signal(false),
            allowRequestQuote: params.allowRequestQuote ?? signal(true),
        });

        if (!params.serviceTier?.trim()) {
            throw new Error('EoneCapacityExpansionRenderInfo: serviceTier is required and must be non-empty');
        }
        if (params.currentReserveTib === undefined || params.currentReserveTib === null) {
            throw new Error('EoneCapacityExpansionRenderInfo: currentReserveTib is required');
        }
        if (params.recommendedReserveTib === undefined || params.recommendedReserveTib === null) {
            throw new Error('EoneCapacityExpansionRenderInfo: recommendedReserveTib is required');
        }

        this.serviceTier = params.serviceTier;
        this.currentReserveTib = params.currentReserveTib;
        this.recommendedReserveTib = params.recommendedReserveTib;
        this.comparisonRows = params.comparisonRows;
        this.projectedUsagePercent = params.projectedUsagePercent;
        this.thresholdHorizonDays = params.thresholdHorizonDays;
        this.daysToExceedCurrentReserve = params.daysToExceedCurrentReserve;
        this.chartOptions = params.chartOptions ?? {};
        this.sparklineOptions = params.sparklineOptions;
        this.asOfTime = params.asOfTime ?? 0;
    }
}

/**
 * ================================================
 * Additional render info for NVMe Now recommendations (prorec-nvme-now)
 * ================================================
 *
 * Awareness-only variant — no recommended model, no target capacity, no
 * capacity/load timeseries. Card body just shows the trade-up graphic
 * and a call-to-action; dialog body shows an Appliance Details table
 * built entirely from `detailRows`, which the processing service bakes
 * with all display strings pre-formatted so the plugin renders them
 * directly with no per-change-detection cost.
 */
export type NvmeNowDetailRow = {
    label: string;
    /**
     * One or more display lines rendered under the same label. Multi-value
     * rows (e.g. Affected components: "2× SAS Flash Module") produce one
     * line per entry; single-value rows have exactly one entry.
     */
    values: string[];
};

export class NvmeNowRenderInfo extends BaseRecommendationRenderInfo {
    readonly detailRows: NvmeNowDetailRow[];

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        detailRows: NvmeNowDetailRow[];
        allowDismiss?: WritableSignal<boolean>;
        allowContactAccountTeam?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            allowDismiss: params.allowDismiss ?? signal(false),
            allowContactAccountTeam: params.allowContactAccountTeam ?? signal(true),
            // Card shows no action buttons — Contact Account Team lives inside the dialog.
            hideActionButtons: true,
            // Card doesn't render the "NVMe Now" pill — the plugin body's marketing
            // message + trade-up glyph already convey the recommendation type.
            hideRecommendationHeader: true,
            // Controller icon above the target name — mirrors the legacy
            // UpgradeToForever/ForeverNow card layout which uses the same glyph.
            topIconSvg: 'icon-controller-rec.svg',
        });

        this.detailRows = params.detailRows;
    }
}

/**
 * ================================================
 * Additional render info for workflow recipe recommendations
 * ================================================
 */

/**
 * Render info for the workflow-recipe program (`prorec-eone-workflow-recipe`).
 *
 * Granularity mirrors the payload: ONE card per signal, listing the N recipes that
 * signal recommends. The card header is fixed copy per the Figma — "Workflow
 * Orchestration" over a green "Recommended Update" — not a per-target entity name.
 *
 * `signalTitle` carries the per-signal title the card header no longer has room for
 * (e.g. "Recommended Recipe for Evergreen//One"). The dialog titles from it, because
 * that is what tells the user WHY the card appeared.
 *
 * Recipes are carried raw — no display strings are baked here. The card and dialog
 * plugins decide how many to show and how to render a recipe whose `title`,
 * `description` or `workatoConnectorIds` is absent.
 *
 * "Import Recipe" is wired in the dialog: `manifestId` fetches the catalog's detail for
 * that manifest, which is a structural superset of what the import dialog takes.
 *
 * An earlier note here claimed the payload also documents a "View in Catalog" action.
 * That phrase appears nowhere — not in the resolver, not in the design, not in this
 * repo — so it has been dropped rather than carried forward. If such an action is ever
 * specified it will need a route: `/workflows/catalog` reads no route or query params,
 * and the detail panel only opens from an in-page click.
 */
export class WorkflowRecipeRenderInfo extends BaseRecommendationRenderInfo {
    readonly recipes: WorkflowRecipe[];

    /** Raw signal off the wire, e.g. 'EVERGREEN_ONE'. The card picks its glyph from it. */
    readonly signal: string;

    /** Per-signal title, shown as the dialog heading. See the class doc. */
    readonly signalTitle: string;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        signal: string;
        signalTitle: string;
        recipes: WorkflowRecipe[];
        allowDismiss?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            allowDismiss: params.allowDismiss ?? signal(false),
            // No quote/account-team path exists for recipes, so the shell's quote row
            // would be empty. Import is offered in the dialog instead.
            hideActionButtons: true,
            // The mock gives this card a Details button; it opens the same dialog the
            // card click does.
            showDetailsButton: true,
            // The recipe mock puts the emphasis line under the target name and the panel
            // content at the top; opting in keeps that layout off every other card.
            emphasisInHeader: true,
        });

        if (!params.recipes?.length) {
            throw new Error('WorkflowRecipeRenderInfo: at least one recipe is required');
        }

        this.recipes = params.recipes;
        this.signal = params.signal;
        this.signalTitle = params.signalTitle;
    }
}

/**
 * ================================================
 * Additional render info for EverCycle Buyback recommendations
 * (prorec-evercycle-buyback)
 * ================================================
 *
 * Rendering rule (per CUJ 002 spec): the EverCycle option is actionable only
 * when `valuation` is non-null and `valuation.validUntilUtc` is in the future.
 * Otherwise the card and dialog render "Valuation pending" and the Renew
 * options stay enabled. The card plugin / dialog plugin derive this via
 * `isValuationActionable`; the service that builds the render-info is the
 * source of truth for `valuation` and `validUntilUtc`.
 *
 * `evercycleBuybackEligible` on an appliance row is a separate model field (see
 * `subscription-assessment-appliance.ts`); it is unrelated to that row's
 * `conversionCreditEligible` / `conversionCreditEligibleArrayIds`, which carry the
 * Evergreen//One conversion-credit concept.
 */

export type EvercycleProgram = 'evercycle-buyback-fa' | 'evercycle-buyback-fb';

export type EvercycleScope = 'FULL_APPLIANCE' | 'COMPONENT_ONLY';

/** Reason codes for component-level exclusion; copy comes from a lookup table in the plugin. */
export type EvercycleExclusionReason = 'XR2_CONTROLLER' | 'SAS_DATAPACK' | 'PRE_FB_S_GENERATION';

export interface EvercycleExcludedComponent {
    readonly label: string;
    readonly reason: EvercycleExclusionReason;
}

export interface EvercycleValuation {
    readonly offerId: string;
    /** Raw amount in `currency` units (e.g. 42500). Format in the template with `| currency`. */
    readonly totalAmount: number;
    /** ISO 4217 currency code (e.g. "USD"). */
    readonly currency: string;
    /** Pre-bucketed label for telemetry — kept to keep Amplitude cardinality low. */
    readonly totalAmountBucket: '<10k' | '10k-50k' | '50k-100k' | '>100k';
    /** When the valuation itself expires. Shown to the user as the offer-validity date. */
    readonly validUntilUtc: moment.Moment;
    /**
     * The server-side grace window during which Accept still succeeds; typically
     * a few days later than `validUntilUtc` (see recommendation-service's
     * `BuybackPayloadTest`, e.g. 10-10 valid vs 10-17 actionable). When present,
     * the UI gates actionability on this timestamp so a dialog opened within
     * the grace window matches what the server will accept. Falls back to
     * `validUntilUtc` when omitted (older payloads).
     */
    readonly actionableUntilUtc?: moment.Moment;
}

/**
 * The three options the dialog surfaces, matching the spec's
 * "Renew as-is / Renew with Forever upgrade / EverCycle Buyback" tri-choice.
 */
export type EvercycleOption = 'RENEW_AS_IS' | 'RENEW_WITH_FOREVER' | 'EVERCYCLE_BUYBACK';

export class EvercycleBuybackRenderInfo extends BaseRecommendationRenderInfo {
    readonly program: EvercycleProgram;
    readonly orgId: string;
    readonly recommendationId: string;
    readonly scope: EvercycleScope;
    readonly excludedComponents: readonly EvercycleExcludedComponent[];
    /** Populated once the BI offer table has a matching row for this recommendation; null until then (M1). */
    readonly valuation: EvercycleValuation | null;
    readonly documentationUrl: string;
    /** When the appliance's subscription expired (UTC); null when the item does not say. */
    readonly subscriptionExpiredAtUtc: moment.Moment | null;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        program: EvercycleProgram;
        orgId: string;
        recommendationId: string;
        scope: EvercycleScope;
        excludedComponents?: readonly EvercycleExcludedComponent[];
        valuation?: EvercycleValuation | null;
        documentationUrl: string;
        subscriptionExpiredAtUtc?: moment.Moment | null;
        allowDismiss?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            allowDismiss: params.allowDismiss ?? signal(false),
        });

        if (!params.program) {
            throw new Error('EvercycleBuybackRenderInfo: program is required');
        }
        if (!params.orgId) {
            throw new Error('EvercycleBuybackRenderInfo: orgId is required');
        }
        if (!params.recommendationId) {
            throw new Error('EvercycleBuybackRenderInfo: recommendationId is required');
        }
        if (!params.scope) {
            throw new Error('EvercycleBuybackRenderInfo: scope is required');
        }
        if (!params.documentationUrl?.trim()) {
            throw new Error('EvercycleBuybackRenderInfo: documentationUrl is required');
        }

        this.program = params.program;
        this.orgId = params.orgId;
        this.recommendationId = params.recommendationId;
        this.scope = params.scope;
        this.excludedComponents = params.excludedComponents ?? [];
        this.valuation = params.valuation ?? null;
        this.documentationUrl = params.documentationUrl;
        this.subscriptionExpiredAtUtc = params.subscriptionExpiredAtUtc ?? null;
    }

    /**
     * The EverCycle action is actionable only when a valuation exists AND the
     * server's acceptance window has not elapsed. The acceptance window is
     * `valuation.actionableUntilUtc` when the server supplied it (grace window
     * past the display validity), otherwise `valuation.validUntilUtc`. This
     * matches CUJ 002's "rendering rule" contract and MUST NOT be softened —
     * expired or missing valuations must render as "Valuation pending" and
     * keep Renew paths enabled.
     *
     * Takes an explicit `now` argument so a `computed()` caller can declare a
     * clock-signal dependency; without that, a `now()` captured inside this
     * method would be cached by Angular's change detector and the dialog would
     * stay actionable past expiry.
     */
    isValuationActionableAt(now: moment.Moment): boolean {
        if (!this.valuation) {
            return false;
        }
        const deadline = this.valuation.actionableUntilUtc ?? this.valuation.validUntilUtc;
        return deadline.isAfter(now);
    }
}

/**
 * ================================================
 * Support contract renewal, shared by every card that carries an end-of-support-contract recommendation
 * ================================================
 */

/** The renewal state of one end-of-support-contract recommendation, built once per card. */
export interface EoscRenewal {
    /** Id of the end-of-support-contract recommendation the renewal is for. */
    readonly recommendationId: string;
    /** Whether the user may request a contract renewal. */
    readonly allowRenew: Signal<boolean>;
}

export function createEoscRenewal(recommendationId: string, allowRenew: Signal<boolean>): EoscRenewal {
    return { recommendationId, allowRenew };
}

/**
 * ================================================
 * Additional render info for the FlashBlade end-of-life recommendation (prorec-eol-fb-norway)
 * ================================================
 */

/**
 * Render info for the FlashBlade end-of-life card.
 *
 * The card states one fact — the appliance is end of life — and counts down the days to
 * the end-of-support date. The model and the date itself are shown in the dialog. There is
 * no recommended configuration to offer, so no quote or request-quote action is enabled.
 */
export class FbNorwayEolRenderInfo extends BaseRecommendationRenderInfo {
    readonly currentModel: string;
    /**
     * End-of-support date, or null when the payload carries none. The card counts the days
     * to it and the dialog formats it.
     */
    readonly eolDate: Date | null;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        currentModel: string;
        eolDate: Date | null;
        allowDismiss?: WritableSignal<boolean>;
        allowContactAccountTeam?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            allowDismiss: params.allowDismiss ?? signal(false),
            allowContactAccountTeam: params.allowContactAccountTeam ?? signal(true),
            // Card shows no action buttons — Contact Account Team lives inside the dialog.
            hideActionButtons: true,
            // Card doesn't render the "End of Life" pill — the plugin body's icon, red
            // statement and day count already convey the recommendation type, matching the
            // layout of the older end-of-life cards.
            hideRecommendationHeader: true,
        });

        this.currentModel = params.currentModel;
        this.eolDate = params.eolDate;
    }
}

/**
 * ================================================
 * Additional render info for the FlashBlade end-of-support-contract recommendation
 * (prorec-end-of-support-contract-fb)
 * ================================================
 */

/**
 * Render info for the FlashBlade end-of-support-contract card.
 *
 * The card states that the support contract has expired and points to renewing it. The
 * dialog carries the renewal notice and the Renew button.
 */
export class EoscFbRenderInfo extends BaseRecommendationRenderInfo {
    readonly eosc: EoscRenewal;

    constructor(params: {
        cardPluginComponentName: string;
        dialogPluginComponentName: string;
        id: string;
        targetName: string;
        recommendationHeader: {
            label: string;
            emphasis: RecommendationHeaderEmphasis;
        };
        eosc: EoscRenewal;
        allowDismiss?: WritableSignal<boolean>;
    }) {
        super({
            cardPluginComponentName: params.cardPluginComponentName,
            dialogPluginComponentName: params.dialogPluginComponentName,
            id: params.id,
            targetName: params.targetName,
            recommendationHeader: params.recommendationHeader,
            allowDismiss: params.allowDismiss ?? signal(false),
            // Card shows no action buttons — Renew lives inside the dialog.
            hideActionButtons: true,
            // The plugin body's icon and red "Expired" heading convey the recommendation type.
            hideRecommendationHeader: true,
        });

        this.eosc = params.eosc;
    }
}
